import { Head, Link } from '@inertiajs/react'
import { withWorkspaceLayout } from '@/app/layouts/WorkspaceLayout'
import { type PageNode } from '@/widgets/sidebar/PageTree'

interface ChangeRequestItem {
  id: number
  action: 'edit' | 'create_page' | 'create_folder'
  page: { id: number; title: string; slug: string; path: string; breadcrumb: string } | null
  proposed_title: string | null
  proposed_parent: { id: number; title: string; path: string; breadcrumb: string } | null
  content_md: string | null
  author: { name: string }
  status: string
  source: string
  batch_id: string | null
  created_at: string
  reviewer_comment: string | null
}

interface Batch {
  batch_id: string
  source: string
  author: { name: string }
  created_at: string
  items: ChangeRequestItem[]
}

interface ChangeRequestsProps {
  workspace: { slug: string; name: string }
  sidebar_pages: PageNode[]
  change_requests: ChangeRequestItem[]
  batches: Batch[]
}

function crTitle(cr: ChangeRequestItem): string {
  return cr.action === 'edit' ? cr.page?.title || 'Untitled' : cr.proposed_title || 'Untitled'
}

function ActionBadge({ action }: { action: string }) {
  const colors = action === 'edit'
    ? 'bg-blue-50 text-blue-700 ring-blue-600/20'
    : 'bg-green-50 text-green-700 ring-green-600/20'
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${colors}`}>
      {action === 'edit' ? 'Edit' : action === 'create_page' ? 'New page' : 'New folder'}
    </span>
  )
}

function ChangeRequests({ workspace, change_requests, batches }: ChangeRequestsProps) {
  const hasItems = change_requests.length > 0 || batches.length > 0

  return (
    <>
      <Head title="Change Requests" />
      <div className="mx-auto max-w-4xl px-6 py-8">
        <h2 className="text-2xl font-bold text-gray-900">Pending Change Requests</h2>

        {!hasItems ? (
          <div className="mt-12 text-center">
            <p className="text-lg text-gray-500">No pending change requests.</p>
            <p className="mt-2 text-sm text-gray-400">
              Change requests from viewers will appear here for review.
            </p>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {batches.map((batch) => (
              <div
                key={batch.batch_id}
                className="rounded-lg border border-amber-200 bg-amber-50 p-5 shadow-sm"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Batch — {batch.items.length} changes
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {batch.source} by {batch.author.name} on{' '}
                      {new Date(batch.created_at).toLocaleDateString()}
                    </p>
                    <ul className="mt-2 space-y-1">
                      {batch.items.map((cr) => (
                        <li key={cr.id} className="flex items-center gap-2 text-sm text-gray-700">
                          <ActionBadge action={cr.action} />
                          <span>
                            {crTitle(cr)}
                            {cr.action === 'edit' && cr.page?.breadcrumb && (
                              <span className="ml-1.5 text-xs text-gray-400">{cr.page.breadcrumb}</span>
                            )}
                            {cr.action !== 'edit' && cr.proposed_parent && (
                              <span className="ml-1.5 text-xs text-gray-400">in {cr.proposed_parent.breadcrumb}</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Link
                    href={`/${workspace.slug}/change_requests/${batch.items[0].id}`}
                    className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                  >
                    Review
                  </Link>
                </div>
              </div>
            ))}

            {change_requests.map((cr) => (
              <div
                key={cr.id}
                className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {crTitle(cr)}
                      </h3>
                      <ActionBadge action={cr.action} />
                    </div>
                    {cr.action === 'edit' && cr.page?.breadcrumb && (
                      <p className="mt-0.5 text-xs text-gray-400">{cr.page.breadcrumb}</p>
                    )}
                    {cr.action !== 'edit' && cr.proposed_parent && (
                      <p className="mt-0.5 text-xs text-gray-400">in {cr.proposed_parent.breadcrumb}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      Submitted by {cr.author.name} on{' '}
                      {new Date(cr.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <Link
                    href={`/${workspace.slug}/change_requests/${cr.id}`}
                    className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                  >
                    Review
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

ChangeRequests.layout = withWorkspaceLayout
export default ChangeRequests
